import { createSelector } from "@reduxjs/toolkit";
import { getServiceIcon } from "Components/ServiceIcon/helper";
import { getResourceProfile } from "Constants/constants";
import { setDeep } from "Libs/objectAccess";
import { objectEntries, sortAlphabetically } from "Libs/utils";
import { selectEnableGuaranteedResources } from "Reducers/project/capabilities";
import { gitProjectSelector } from "Reducers/project/project";
import {
  projectSettingsSizingApiSelector,
  supportOCIImagesSelector
} from "Reducers/projectSettings";
import {
  getDeploymentServices,
  getDeploymentWebApps
} from "src/organization/common/containers/ServicesDisplay/util/serviceGraph";

import {
  BLACKFIRE_ID_VARIABLE,
  CURRENT_DEPLOYMENT_ID,
  NEXT_DEPLOYMENT_ID,
  isSettledDeploymentErrorCode
} from "./constants";
import {
  deploymentResourcesHandler,
  resourcesAccumulatorHandler,
  resourcesFormatHandler,
  retrieveOneResourceRawDataHandler,
  checkNullProfileSize,
  parseUTCDate,
  hasSunsetWarningTriggered
} from "./util";

import type {
  DeploymentLocalDedicated,
  DeploymentService
} from "Api/organizations/projects/environments/deployments/types";
import type { RootState } from "Store/configureStore";
import type { AppRelationshipsTransformedType } from "../service";
import type {
  AppAndServiceProps,
  DeprecatedImageEntry,
  ResourceCollectionType,
  ServiceDecodedParams
} from "./types";

type BasicDeploymentSelectorParams = {
  organizationId?: string;
  projectId?: string;
  environmentId?: string;
};

export const deploymentStateSelector = (state: RootState) => state.deployment;

// Helper to create a unique key for tracking fetched environments
const getEnvironmentKey = (
  organizationId: string,
  projectId: string,
  environmentId: string
) => `${organizationId}/${projectId}/${environmentId}`;

/**
 * Selector to check if deployments have been fetched for a specific environment.
 * Returns true only when BOTH current and next deployments have been fetched.
 * This is keyed by environment, so it returns the correct state immediately on navigation.
 */
export const isEnvironmentDeploymentsFetchedSelector = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { organizationId, projectId, environmentId }) => {
    if (!organizationId || !projectId || !environmentId) {
      return false;
    }
    const envKey = getEnvironmentKey(organizationId, projectId, environmentId);
    const fetched = deploymentState?.fetchedEnvironments?.[envKey];
    return fetched?.current === true && fetched?.next === true;
  }
);

/**
 * Has the current deployment been asked for on this environment yet?
 *
 * `fetchedEnvironments` is stamped on success and on a settled failure (404,
 * 403), so this answers "we already have the API's verdict", not "we have a
 * deployment". An environment that never deployed answers 404 forever;
 * without this the fetch re-fires on every re-render (CON-1655). Transient
 * failures are not stamped, so they stay retryable.
 */
export const isCurrentDeploymentFetchedSelector = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { organizationId, projectId, environmentId }) => {
    if (!organizationId || !projectId || !environmentId) {
      return false;
    }
    const envKey = getEnvironmentKey(organizationId, projectId, environmentId);
    return deploymentState?.fetchedEnvironments?.[envKey]?.current === true;
  }
);

/** Next-deployment counterpart of `isCurrentDeploymentFetchedSelector`. */
export const isNextDeploymentFetchedSelector = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { organizationId, projectId, environmentId }) => {
    if (!organizationId || !projectId || !environmentId) {
      return false;
    }
    const envKey = getEnvironmentKey(organizationId, projectId, environmentId);
    return deploymentState?.fetchedEnvironments?.[envKey]?.next === true;
  }
);

/**
 * Has the deployment settings endpoint given its final answer on this
 * environment? Either it returned settings, or it failed with a code it will
 * keep returning. A transient failure counts as unanswered, so the next mount
 * retries — otherwise one 500 hides the manual-deployment controls for the
 * whole session.
 */
export const isDeploymentSettingsFetchedSelector = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { organizationId, projectId, environmentId }) => {
    if (!organizationId || !projectId || !environmentId) {
      return false;
    }
    const hasSettings =
      deploymentState?.settings?.[organizationId]?.[projectId]?.[
        environmentId
      ] !== undefined;
    const settledWithError = isSettledDeploymentErrorCode(
      deploymentState?.responseCode?.[organizationId]?.[projectId]?.[
        environmentId
      ]?.settings
    );

    return hasSettings || settledWithError;
  }
);

export const deploymentDataSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.data
);

export const selectDeployment = createSelector(
  deploymentDataSelector,
  gitProjectSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentData, project, { organizationId, projectId, environmentId }) => {
    const envId = environmentId || project?.default_branch;

    if (!organizationId || !projectId || !envId) {
      return;
    }

    return deploymentData?.[organizationId]?.[projectId]?.[envId];
  }
);

export const currentDeploymentSelector = createSelector(
  selectDeployment,
  deployment => deployment?.[CURRENT_DEPLOYMENT_ID]
);

export const nextDeploymentSelector = createSelector(
  selectDeployment,
  deployment => deployment?.[NEXT_DEPLOYMENT_ID]
);

export const deploymentHasLanguageSelector = createSelector(
  currentDeploymentSelector,
  (_: RootState, params: { languages: string[] }) => params,
  (currentDeployment, { languages = [] }) => {
    const webapps = currentDeployment?.webapps || {};

    return Object.keys(webapps || {}).some(appName =>
      languages.some(supportType => {
        const appType =
          currentDeployment?.webapps[appName].type.toLowerCase?.();
        return appType?.indexOf(supportType.toLowerCase?.()) !== -1;
      })
    );
  }
);

export const resourcesTypeSelector = createSelector(
  currentDeploymentSelector,
  deployment => {
    const webapps = Object.entries(deployment?.webapps ?? {});
    const workers = Object.entries(deployment?.workers ?? {});
    const services = Object.entries(deployment?.services ?? {});

    const allResources = [...webapps, ...workers, ...services];

    return allResources.reduce<{ [serviceName: string]: boolean }>(
      (acc, [key, cur]) => {
        const isGuaranteed = cur?.resources?.profile_size?.endsWith(".gc");
        acc[key] = !!isGuaranteed;
        return acc;
      },
      {}
    );
  }
);

export const currentMetricsServicesSelector = createSelector(
  currentDeploymentSelector,
  currentDeployment => {
    if (!currentDeployment) {
      return {};
    }

    const webapps = currentDeployment.webapps;
    const workers = currentDeployment.workers;
    const services = Object.fromEntries(
      sortAlphabetically(
        Object.entries(currentDeployment.services),
        ([key]) => key
      )
    );

    return Object.fromEntries(
      Object.entries({
        ...webapps,
        ...services,
        ...workers
      }).map(
        ([key, value]: [string, DeploymentService & { icon?: string }]) => {
          const icon = value?.type.split(":")[0];
          value.icon = icon;
          value.name = key;
          return [key, value];
        }
      )
    );
  }
);

export const currentDeploymentHasGuaranteedResourcesSelector = createSelector(
  currentDeploymentSelector,
  deployment => {
    if (!deployment) return;

    const webApps = Object.values(deployment?.webapps ?? {});
    const services = Object.values(deployment?.services ?? {});
    const workers = Object.values(deployment?.workers ?? {});

    const serviceWithGuaranteedResources = [
      ...webApps,
      ...services,
      ...workers
    ].find(service => /\.gc$/.test(service?.resources?.profile_size || ""));

    return !!serviceWithGuaranteedResources;
  }
);

export const loadingNextDeploymentSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.loadingNext
);

export const loadingCurrentDeploymentSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.loadingCurrent
);

export const isLoadingDeploymentSettingsSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.loadingSettings
);

export const isLoadingSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState.loading
);

export const isUpdateLoadingSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState.updateLoading
);

export const deploymentBaseErrorSelector = createSelector(
  deploymentStateSelector,
  deployment => deployment?.errors
);

export const generalDeploymentErrorSelector = createSelector(
  deploymentBaseErrorSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentErrors, { organizationId, projectId, environmentId }) => {
    if (organizationId && projectId && environmentId) {
      return deploymentErrors?.[organizationId]?.[projectId]?.[environmentId];
    }
  }
);

export const currentDeploymentErrorSelector = createSelector(
  generalDeploymentErrorSelector,
  environmentErrorObj => environmentErrorObj?.[CURRENT_DEPLOYMENT_ID]
);

export const nextDeploymentErrorSelector = createSelector(
  generalDeploymentErrorSelector,
  environmentErrorObj => environmentErrorObj?.[NEXT_DEPLOYMENT_ID]
);
export const deploymentErrorSelector = createSelector(
  currentDeploymentErrorSelector,
  nextDeploymentErrorSelector,
  (currentError, nextError) => {
    if (currentError?.message) return currentError;
    if (nextError?.message) return nextError;
    return undefined;
  }
);

export const updateDeploymentSettingsErrorSelector = createSelector(
  generalDeploymentErrorSelector,
  environmentErrorObj => environmentErrorObj?.settings
);

export const selectDeploymentSettings = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { projectId, environmentId, organizationId }) => {
    if (organizationId && projectId && environmentId) {
      return deploymentState?.settings?.[organizationId]?.[projectId]?.[
        environmentId
      ];
    }
  }
);

export const isManualDeploymentEnabledSelector = createSelector(
  selectDeploymentSettings,
  deploymentSettings => {
    return deploymentSettings?.enable_manual_deployments;
  }
);

export const responseCodesSelector = createSelector(
  deploymentStateSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentState, { organizationId, projectId, environmentId }) => {
    if (organizationId && projectId && environmentId) {
      return deploymentState?.responseCode?.[organizationId]?.[projectId]?.[
        environmentId
      ];
    }
  }
);

export const deploymentStatusCodesSelector = createSelector(
  responseCodesSelector,
  responseCodes => {
    const { current, next } = responseCodes ?? {
      current: undefined,
      next: undefined
    };

    return { current, next };
  }
);

export const deploymentVariablesSelector = createSelector(
  deploymentDataSelector,
  gitProjectSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (deploymentData, project, { organizationId, projectId, environmentId }) => {
    const envId = environmentId ?? project?.default_branch;
    if (organizationId && projectId && envId) {
      return deploymentData?.[organizationId]?.[projectId]?.[envId]?.[
        CURRENT_DEPLOYMENT_ID
      ]?.variables;
    }
  }
);

export const blackfireEnabledInEnvironmentSelector = createSelector(
  deploymentVariablesSelector,
  variables =>
    variables?.some(variable => variable.name === BLACKFIRE_ID_VARIABLE)
);

export const blackfireUUIDSelector = createSelector(
  deploymentVariablesSelector,
  variables =>
    variables?.find(variable => variable.name === BLACKFIRE_ID_VARIABLE)?.value
);

export const allEnvironmentsResource = createSelector(
  deploymentDataSelector,
  (_: RootState, params: { organizationId: string; projectId: string }) =>
    params,
  (deploymentData, { organizationId, projectId }) => {
    const environmentLevel = deploymentData?.[organizationId]?.[projectId];
    const resources = Object.values(environmentLevel ?? {}).map(
      deploymentObj => deploymentObj?.[CURRENT_DEPLOYMENT_ID]
    );

    return resources?.filter(resource => typeof resource !== "undefined");
  }
);

export const deploymentServicesSelector = createSelector(
  deploymentStateSelector,
  deployment => deployment.services
);

export const currentDeploymentServicesSelector = createSelector(
  deploymentServicesSelector,
  (_: RootState, params: BasicDeploymentSelectorParams) => params,
  (currentDeploymentServices, { organizationId, projectId, environmentId }) => {
    if (organizationId && projectId && environmentId) {
      return currentDeploymentServices?.[organizationId]?.[projectId]?.[
        environmentId
      ];
    }
  }
);

export const selectCurrentService = createSelector(
  currentDeploymentServicesSelector,
  (_: RootState, params: ServiceDecodedParams) => params,
  (
    currentDeploymentData,
    { appName }
  ):
    | {
        class: string;
        icon?: string;
        name: string;
        type: string;
        relationships: Record<string, AppRelationshipsTransformedType>;
      }
    | undefined => {
    const dataService =
      currentDeploymentData?.[appName] ??
      currentDeploymentData?.["webapps"]?.[appName] ??
      currentDeploymentData?.["services"]?.[appName];

    return dataService;
  }
);

export const selectCurrentServiceWorkers = createSelector(
  currentDeploymentServicesSelector,
  (_: RootState, params: ServiceDecodedParams) => params,
  (currentDeploymentData, { appName }) => {
    const workers = currentDeploymentData?.["workers"] || [];

    const serviceWorkers = Object.values(workers).filter(worker =>
      worker?.name.startsWith(`${appName}--`)
    );

    return serviceWorkers;
  }
);

export const selectCurrentServiceCronJobs = createSelector(
  selectCurrentService,
  appNameService => {
    const crons = appNameService?.["crons"] ?? {};

    const cronsEntries = Object.entries(crons);
    if (!cronsEntries.length) return [];

    const serviceCronJobs = cronsEntries.map(([key, value]) => ({
      ...(value as object),
      name: key
    }));

    return serviceCronJobs;
  }
);

export const currentAppAndServiceSelector = createSelector(
  currentDeploymentSelector,
  (_: RootState, params: AppAndServiceProps) => params,
  (currentDeployment, { appName, key }) =>
    appName ? currentDeployment?.[key]?.[appName] : undefined
);

export const emptyDeprecatedImages = {
  decommissionedImages: [] as DeprecatedImageEntry[],
  sunsetImages: [] as DeprecatedImageEntry[],
  hasDecommissionedImages: false,
  hasSunsetImages: false,
  byEnvironment: {} as Record<
    string,
    {
      decommissioned: DeprecatedImageEntry[];
      sunset: DeprecatedImageEntry[];
    }
  >
};

const deploymentImagesSelector = (state: RootState) => state.deployment?.images;

export const deprecatedImagesSelector = createSelector(
  deploymentImagesSelector,
  (_: RootState, projectId: string) => projectId,
  (imagesState, projectId) => {
    const projectEnvs = imagesState?.data?.[projectId];
    if (!projectEnvs) return emptyDeprecatedImages;

    const decommissionedImages: DeprecatedImageEntry[] = [];
    const sunsetImages: DeprecatedImageEntry[] = [];
    const byEnvironment: Record<
      string,
      { decommissioned: DeprecatedImageEntry[]; sunset: DeprecatedImageEntry[] }
    > = {};
    for (const [environmentId, images] of Object.entries(projectEnvs)) {
      for (const category of ["webapps", "workers", "services"] as const) {
        for (const [serviceName, service] of Object.entries(
          images[category] ?? {}
        )) {
          if (!service?.deprecation) continue;

          const version = service.id.split(":")[1];
          if (!version) continue;
          const upstreamVersion =
            service.deprecation.upstream_versions?.[version];
          // new Date() inside a selector is a pragmatic tradeoff: the classification can shift over time without a Redux action, but createSelector still memoizes as long as the input data is stable.
          const now = new Date();
          const sunsetAt = upstreamVersion?.sunset_at
            ? parseUTCDate(upstreamVersion.sunset_at)
            : undefined;

          const isSunsetPassed = sunsetAt
            ? sunsetAt <= now
            : service.deprecation.status === "sunset";
          const decommissionedAt = upstreamVersion?.decommissioned_at
            ? parseUTCDate(upstreamVersion.decommissioned_at)
            : undefined;
          const isDecommissioned =
            service.deprecation.status === "decommissioned" &&
            (!decommissionedAt || decommissionedAt <= now);

          const entry: DeprecatedImageEntry = {
            id: service.id,
            status: service.deprecation.status,
            environmentId,
            version,
            upstreamVersion,
            serviceName,
            isSunsetPassed
          };

          if (!byEnvironment[environmentId]) {
            byEnvironment[environmentId] = { decommissioned: [], sunset: [] };
          }

          if (isDecommissioned) {
            decommissionedImages.push(entry);
            byEnvironment[environmentId].decommissioned.push(entry);
          } else if (
            (service.deprecation.status === "active" &&
              !!sunsetAt &&
              hasSunsetWarningTriggered(sunsetAt)) ||
            service.deprecation.status === "sunset" ||
            service.deprecation.status === "decommissioned"
          ) {
            // Status is "sunset", or "decommissioned" but decommissioned_at
            // hasn't passed yet — show as sunset so the sticker can display
            // the "no longer supported" message with days remaining.
            sunsetImages.push(entry);
            byEnvironment[environmentId].sunset.push(entry);
          }
        }
      }
    }

    return {
      decommissionedImages,
      sunsetImages,
      hasDecommissionedImages: decommissionedImages.length > 0,
      hasSunsetImages: sunsetImages.length > 0,
      byEnvironment
    };
  }
);
export const imageStatusByServiceSelector = createSelector(
  deprecatedImagesSelector,
  (_: RootState, _projectId: string, environmentId: string) => environmentId,
  (imageData, environmentId) => {
    const envData = imageData.byEnvironment[environmentId];
    if (!envData)
      return {} as Record<
        string,
        { isSunset: boolean; isDecommissioned: boolean }
      >;

    const statusMap: Record<
      string,
      { isSunset: boolean; isDecommissioned: boolean }
    > = {};

    for (const entry of envData.decommissioned) {
      statusMap[entry.id] = { isDecommissioned: true, isSunset: false };
    }
    for (const entry of envData.sunset) {
      if (!statusMap[entry.id] && entry.isSunsetPassed) {
        statusMap[entry.id] = {
          isDecommissioned: false,
          isSunset: true
        };
      }
    }

    return statusMap;
  }
);

export const currentAppSelector = createSelector(
  currentAppAndServiceSelector,
  (_: RootState, params: AppAndServiceProps) => params,
  supportOCIImagesSelector,
  (state: RootState, params: AppAndServiceProps) =>
    deprecatedImagesSelector(state, params.projectId),

  (app, { appName, key }, supportOCIImages, imageData) => {
    if (key === "webapps" && appName) {
      const { iconName } = getServiceIcon(
        app as DeploymentService,
        undefined,
        supportOCIImages
      );

      const a = getDeploymentWebApps({
        name: appName,
        iconName,
        currentLine: 0,
        column: 0,
        app: app as DeploymentService,
        decommissionedImages: imageData.decommissionedImages,
        sunsetImages: imageData.sunsetImages
      });

      return a;
    }
  }
);

export const currentServiceSelector = createSelector(
  currentAppAndServiceSelector,
  (_: RootState, params: AppAndServiceProps) => params,
  (state: RootState, params: AppAndServiceProps) =>
    deprecatedImagesSelector(state, params.projectId),
  (service, { appName: serviceName, key }, imageData) => {
    if (key === "services" && serviceName) {
      const type = service?.type?.split(":")?.[1];

      const s = getDeploymentServices({
        name: serviceName,
        type,
        currentLine: 0,
        column: 0,
        service: service as DeploymentService,
        decommissionedImages: imageData.decommissionedImages,
        sunsetImages: imageData.sunsetImages
      });

      return s;
    }
  }
);

export const nonCalculatedOneDeploymentResourceSelector = createSelector(
  currentDeploymentSelector,
  (
    _: RootState,
    params: {
      resourceType: ResourceCollectionType;
      appName: string;
    }
  ) => params,
  (currentDeployment, { resourceType, appName }) => {
    const {
      cpu,
      ram,
      instances,
      disk,
      status,
      cronsQuantity,
      containerProfile
    } = retrieveOneResourceRawDataHandler({
      currentDeployment,
      resourceType,
      appName
    });

    const currentResourceFormatted = resourcesFormatHandler({
      cpu,
      ram,
      instances,
      disk,
      status
    });

    return {
      ...currentResourceFormatted,
      profileLabel: getResourceProfile(containerProfile ?? ""),
      crons: cronsQuantity
    };
  }
);

export const oneDeploymentResourceSelector = createSelector(
  currentDeploymentSelector,
  (
    _: RootState,
    params: {
      resourceType: ResourceCollectionType;
      appName: string;
    }
  ) => params,
  (currentDeployment, { resourceType, appName }) => {
    const currentResource = {
      cpu: {
        shared: 0,
        guaranteed: 0,
        total: 0
      },
      ram: 0,
      instances: 0,
      disk: 0,
      status: ""
    };
    const {
      cpu,
      ram,
      instances,
      disk,
      status,
      cronsQuantity,
      containerProfile
    } = retrieveOneResourceRawDataHandler({
      currentDeployment,
      resourceType,
      appName
    });

    const currentResourceFormatted = resourcesFormatHandler(
      resourcesAccumulatorHandler(currentResource, {
        cpu,
        ram,
        instances,
        disk,
        status
      })
    );

    return {
      ...currentResourceFormatted,
      profileLabel: getResourceProfile(containerProfile ?? ""),
      crons: cronsQuantity
    };
  }
);

export const deploymentResourcesSelector = createSelector(
  currentDeploymentSelector,
  currentDeployment => deploymentResourcesHandler(currentDeployment)
);

export const deploymentSizingApiEnabledSelector = createSelector(
  currentDeploymentSelector,
  nextDeploymentSelector,
  projectSettingsSizingApiSelector,
  (currentDeployment, nextDeployment, projectSettingSizingApi) => {
    const deployment = currentDeployment ?? nextDeployment;
    const deploymentSizingApi =
      deployment?.project_info?.settings?.sizing_api_enabled;

    if (typeof deploymentSizingApi === "undefined") {
      return projectSettingSizingApi;
    }
    return deploymentSizingApi;
  }
);

export const hasAtLeastLowestCommonProfileSelector = createSelector(
  currentDeploymentSelector,
  nextDeploymentSelector,
  (_: RootState, params: { serviceKey: string; serviceName: string }) => params,
  (currentDeployment, nextDeployment, { serviceKey, serviceName }) => {
    const deployment = currentDeployment ?? nextDeployment;
    const containerProfiles = deployment?.container_profiles;
    const service = deployment?.[serviceKey]?.[serviceName];
    const containerProfileLabel = service?.container_profile;
    const profileSize = service?.resources?.profile_size ?? "";
    const profiles = containerProfiles?.[containerProfileLabel ?? ""];

    const splitProfiles = Object.values(profiles ?? {}).reduce(
      (profile, current) => {
        profile[current.cpu_type][current.cpu] = current;
        return profile;
      },
      {
        shared: {},
        guaranteed: {}
      }
    );

    let lowestCommonProfile = Number.POSITIVE_INFINITY;

    Object.keys(splitProfiles.shared).forEach(key => {
      if (
        splitProfiles.shared[key] &&
        splitProfiles.guaranteed[key] &&
        +key < lowestCommonProfile
      ) {
        lowestCommonProfile = +key;
      }
    });

    const hasAtLeastLowestCommonProfile = +profileSize >= lowestCommonProfile;

    return hasAtLeastLowestCommonProfile;
  }
);

export const isResourceHighCpuUsageSelector = createSelector(
  deploymentSizingApiEnabledSelector,
  selectEnableGuaranteedResources,
  (
    _: RootState,
    params: {
      isGuaranteedAtResourceLevel: boolean;
      unit: string;
      serviceKind?: string;
      averageUsage: number;
      hasAtLeastLowestCommonProfile: boolean;
    }
  ) => params,
  (
    isSizingApiEnabled,
    isGuaranteedResourcesEnabledProjectLevel,
    {
      isGuaranteedAtResourceLevel,
      unit,
      serviceKind,
      averageUsage,
      hasAtLeastLowestCommonProfile
    }
  ) => {
    const isHighCpuUsage =
      isSizingApiEnabled &&
      !isGuaranteedAtResourceLevel &&
      isGuaranteedResourcesEnabledProjectLevel &&
      unit === "cpu" &&
      serviceKind?.toLowerCase() === "service" &&
      averageUsage >= 80 &&
      hasAtLeastLowestCommonProfile;

    return isHighCpuUsage;
  }
);

export const deploymentValueChecksSelector = createSelector(
  currentDeploymentSelector,
  nextDeploymentSelector,
  (currentDeployment, nextDeployment) => {
    const nextValues: Record<string, any> = {};
    const currentValues: Record<string, any> = {};

    const deploymentNext = {
      webapps: nextDeployment?.webapps,
      workers: nextDeployment?.workers,
      services: nextDeployment?.services
    };

    const deploymentCurrent = {
      webapps: currentDeployment?.webapps,
      workers: currentDeployment?.workers,
      services: currentDeployment?.services
    };

    [deploymentNext, deploymentCurrent].forEach((deployment, i) => {
      const deploymentGroup = i === 0 ? nextValues : currentValues;
      objectEntries(deployment).forEach(
        ([resourceCollectionType, resourceCollection]) => {
          objectEntries(resourceCollection ?? {}).forEach(([label, data]) => {
            setDeep(
              deploymentGroup,
              [resourceCollectionType, label, "instance_count"],
              data?.instance_count
            );

            setDeep(
              deploymentGroup,
              [resourceCollectionType, label, "resources", "profile_size"],
              data?.resources?.profile_size
            );

            setDeep(
              deploymentGroup,
              [resourceCollectionType, label, "disk"],
              data?.disk
            );
          });
        }
      );
    });

    return { nextValues, currentValues };
  }
);

export const resourcesDeploymentStatusSelector = createSelector(
  deploymentStatusCodesSelector,
  deploymentValueChecksSelector,

  ({ current: currentCode, next: nextCode }, { nextValues }) => {
    const hasNullProfileSize = checkNullProfileSize({ nextValues });

    if (hasNullProfileSize) {
      return "No resources configured";
    } else if (nextCode?.toString().match(/^[45]/) && currentCode === 200) {
      return "Build failed";
    }
    return "";
  }
);

export const allResourcesHaveProfileSizeSelector = createSelector(
  nextDeploymentSelector,
  nextDeployment => {
    if (typeof nextDeployment === "undefined") {
      return;
    }
    let hasProfileSize = true;
    const resources = {
      webapps: nextDeployment?.webapps ?? {},
      workers: nextDeployment?.workers ?? {},
      services: nextDeployment?.services ?? {}
    };

    Object.values(resources).forEach(resourceCollection => {
      Object.values(resourceCollection).forEach(data => {
        hasProfileSize = hasProfileSize && !!data?.resources?.profile_size;
      });
    });

    return hasProfileSize;
  }
);

export const allAppsSelector = createSelector(
  currentDeploymentSelector,
  currentAppsAndServices => {
    const webappsLabels = Object.keys(currentAppsAndServices?.webapps ?? {});
    const workersLabels = Object.keys(currentAppsAndServices?.workers ?? {});

    return webappsLabels.concat(workersLabels);
  }
);

export const deploymentDataGridSelector = createSelector(
  deploymentStateSelector,
  (deploymentState): DeploymentLocalDedicated | undefined =>
    deploymentState?.data?.["local-dedicated"]
);

export const deploymentDataGridErrorSelector = createSelector(
  deploymentStateSelector,
  deploymentState => deploymentState?.errors?.["local-dedicated"]
);
